import { API_PATH_PREFIX } from '@ayeldo/frontend-core/runtime/env';

const DEFAULT_LOCAL_API_URL = 'http://localhost:3010';

// Export a small helper that can be invoked with an explicit env object in tests.
export function resolveIdpApiBaseUrl(env?: { VITE_API_URL?: string; DEV?: boolean }): string {
  // Prefer an explicit env object when provided (tests), otherwise fall back to import.meta.env.
  const source: { VITE_API_URL?: unknown; DEV?: unknown } = env ?? (import.meta.env as unknown as Record<string, unknown>);
  const configured: unknown = source.VITE_API_URL;
  const trimmed = typeof configured === 'string' ? configured.trim() : '';

  if (trimmed !== '') {
    return trimmed;
  }

  if (source.DEV === true) {
    return DEFAULT_LOCAL_API_URL;
  }

  throw new Error('VITE_API_URL is not configured for production build');
}

export const API_BASE_URL = resolveIdpApiBaseUrl() + API_PATH_PREFIX;

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
};

export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

type FetchOptions = RequestInit & {
  withCredentials?: boolean;
};

async function fetchJSON<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { withCredentials = true, headers, ...rest } = options;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: withCredentials ? (rest.credentials ?? 'include') : 'omit',
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorPayload | null;
    throw new APIError(
      response.status,
      payload?.error?.code ?? 'UNKNOWN_ERROR',
      payload?.error?.message ?? 'An unknown error occurred',
      payload?.error?.details
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export type CredentialSummary = {
  type: 'password' | 'google';
  createdAt: string;
  googleEmail?: string;
};

export type SessionSummary = {
  sessionId: string;
  authMethod: 'password' | 'google';
  authTime: string;
  lastActivityAt: string;
  expiresAt: string;
  current: boolean;
};

export type ProfileResponse = {
  identityId: string;
  email: string;
  emailVerified: boolean;
  name: string;
  credentials: CredentialSummary[];
  createdAt: string;
};

export type ExportResponse = {
  identity: {
    identityId: string;
    email: string;
    name: string;
    emailVerified: boolean;
    createdAt: string;
  };
  credentials: CredentialSummary[];
  sessions: Array<{
    authMethod: 'password' | 'google';
    authTime: string;
  }>;
  exportedAt: string;
};

export type FeaturesResponse = {
  google: boolean;
};

type EmptyResponse = Record<string, never>;

export const idpApi = {
  getFeatures: () => fetchJSON<FeaturesResponse>('/features', { withCredentials: false }),

  googleInitiate: (flowId: string) =>
    fetchJSON<{ authorizeUrl: string }>(
      `/auth/google/initiate?flowId=${encodeURIComponent(flowId)}`
    ),

  signup: (payload: { name: string; email: string; password: string; interactionId?: string }) =>
    fetchJSON<{ identityId: string; email: string; emailVerified: boolean; message: string }>(
      '/identities/signup',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    ),

  authenticate: (payload: {
    email: string;
    password: string;
    flowId?: string;
    captchaToken?: string;
  }) =>
    fetchJSON<{ success: true; redirectUri?: string }>('/auth/authenticate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  googleCallback: (payload: { code: string; flowId: string }) =>
    fetchJSON<{ success: true; redirectUri: string }>('/auth/google/callback', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  verifyEmailUrl: (token: string) =>
    `${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`,

  forgotPassword: (payload: { email: string }) =>
    fetchJSON<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  resetPassword: (payload: { token: string; newPassword: string }) =>
    fetchJSON<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  signOut: () => fetchJSON<{ success: true }>('/account/sign-out', { method: 'POST' }),

  getProfile: () => fetchJSON<ProfileResponse>('/account/profile'),

  updateProfile: (payload: { name: string }) =>
    fetchJSON<ProfileResponse>('/account/profile', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    fetchJSON<{ message: string }>('/account/change-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  linkGoogle: () => fetchJSON<{ redirectUri: string }>('/account/link-google', { method: 'POST' }),

  linkGoogleCallback: (payload: { code: string; identityId: string }) =>
    fetchJSON<{ message: string; credential: CredentialSummary }>('/account/link-google/callback', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  unlinkGoogle: () => fetchJSON<EmptyResponse>('/account/credentials/google', { method: 'DELETE' }),

  listSessions: () => fetchJSON<{ data: SessionSummary[] }>('/account/sessions'),

  revokeSession: (sessionId: string) =>
    fetchJSON<EmptyResponse>(`/account/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    }),

  requestDeleteAccount: (payload: { confirmation: 'DELETE' }) =>
    fetchJSON<{ message: string; deletionDate: string }>('/account/delete', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  cancelDeletion: () =>
    fetchJSON<{ message: string }>('/account/cancel-deletion', {
      method: 'POST',
    }),

  exportAccountData: () => fetchJSON<ExportResponse>('/account/export'),
};
